import React, { createContext, useState, ReactNode, useEffect, useCallback, useRef } from "react";
import { AppState, AppStateStatus } from "react-native";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { invoiceService } from "@/services/invoiceService";
import { estimateService } from "@/services/estimateService";
import { expenseService } from "@/services/expenseService";
import { timecardService } from "@/services/timecardService";
import { shiftsService } from "@/services/shiftService";
import { incidentService } from "@/services/incidentService";
import type { BadgeContextType } from "@/types/providers/badge";

export const BadgeContext = createContext<BadgeContextType | undefined>(undefined);

// Polling interval for badges that don't use realtime subscriptions
const POLLING_INTERVAL_MS = 15_000;

// Delay before refreshing badge after a manual update (allows DB write to settle)
const REFRESH_DELAY_MS = 1_000;

// Format badge count: undefined for 0, "99+" for 100+, string number otherwise
const formatBadgeCount = (count: number): string | undefined => {
  if (count === 0) return undefined;
  if (count >= 100) return "99+";
  return count.toString();
};

// Valid badge keys for type checking
const BADGE_KEYS = [
  "users",
  "contacts",
  "shifts",
  "projects",
  "invoices",
  "estimates",
  "timecards",
  "expenses",
  "incidents",
] as const;
type BadgeKey = (typeof BADGE_KEYS)[number];

// Normalize entity name to badge key (e.g., "Incident" -> "incidents", "Invoice" -> "invoices")
const normalizeToBadgeKey = (name: string): BadgeKey | null => {
  const lower = name.toLowerCase();
  // Check if it's already a valid badge key
  if (BADGE_KEYS.includes(lower as BadgeKey)) return lower as BadgeKey;
  // Try pluralizing
  const plural = `${lower}s`;
  if (BADGE_KEYS.includes(plural as BadgeKey)) return plural as BadgeKey;
  return null;
};

export function BadgeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const permissions = usePermissions();
  const [invoices, setInvoices] = useState<string | undefined>();
  const [timecards, setTimecards] = useState<string | undefined>();
  const [estimates, setEstimates] = useState<string | undefined>();
  const [expenses, setExpenses] = useState<string | undefined>();
  const [incidents, setIncidents] = useState<string | undefined>();
  const [shifts, setShifts] = useState<string | undefined>();

  // Debounce flag to prevent rapid successive fetches
  const lastFetchRef = useRef<{ [key: string]: number }>({});
  const DEBOUNCE_MS = 10000;

  // Fetch badge counts for Phase 1 (critical badges)
  const fetchInvoicesCount = useCallback(async () => {
    const now = Date.now();
    if (lastFetchRef.current.invoices && now - lastFetchRef.current.invoices < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current.invoices = now;

    const count = await invoiceService.getOverdueInvoicesCount();
    setInvoices(formatBadgeCount(count));
  }, []);

  const fetchTimecardCount = useCallback(async () => {
    const now = Date.now();
    if (lastFetchRef.current.timecards && now - lastFetchRef.current.timecards < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current.timecards = now;

    const count = await timecardService.getUnapprovedTimecardsCount();
    setTimecards(formatBadgeCount(count));
  }, []);

  const fetchEstimatesCount = useCallback(async () => {
    const now = Date.now();
    if (lastFetchRef.current.estimates && now - lastFetchRef.current.estimates < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current.estimates = now;

    const count = await estimateService.getPendingEstimatesCount();
    setEstimates(formatBadgeCount(count));
  }, []);

  const fetchExpensesCount = useCallback(async () => {
    const now = Date.now();
    if (lastFetchRef.current.expenses && now - lastFetchRef.current.expenses < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current.expenses = now;

    const count = await expenseService.getUnapprovedExpensesCount();
    setExpenses(formatBadgeCount(count));
  }, []);

  const fetchShiftsCount = useCallback(async () => {
    const now = Date.now();
    if (lastFetchRef.current.shifts && now - lastFetchRef.current.shifts < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current.shifts = now;

    const count = await shiftsService.getShiftsWithIssuesCount();
    // Show actual count without 99+ cap
    setShifts(count === 0 ? undefined : count.toString());
  }, []);

  const fetchIncidentsCount = useCallback(async () => {
    const now = Date.now();
    if (lastFetchRef.current.incidents && now - lastFetchRef.current.incidents < DEBOUNCE_MS) {
      return;
    }
    lastFetchRef.current.incidents = now;

    const count = await incidentService.getUnresolvedIncidentsCount();
    setIncidents(formatBadgeCount(count));
  }, []);

  // Manual refresh function for on-demand updates
  // Accepts either badge keys ("incidents") or entity names ("Incident")
  // Includes a short delay to allow database writes to settle before re-fetching
  const refreshBadge = useCallback(
    async (entityOrBadgeType: string) => {
      const badgeType = normalizeToBadgeKey(entityOrBadgeType);
      if (!badgeType) {
        // Silently ignore unknown badge types
        return;
      }

      // Wait for database write to propagate
      await new Promise((resolve) => setTimeout(resolve, REFRESH_DELAY_MS));

      // Clear debounce so fetch runs immediately
      lastFetchRef.current[badgeType] = 0;

      switch (badgeType) {
        case "invoices":
          await fetchInvoicesCount();
          break;
        case "estimates":
          await fetchEstimatesCount();
          break;
        case "expenses":
          await fetchExpensesCount();
          break;
        case "shifts":
          await fetchShiftsCount();
          break;
        case "incidents":
          await fetchIncidentsCount();
          break;
        case "timecards":
          await fetchTimecardCount();
          break;
        // Add more cases as needed
      }
    },
    [
      fetchInvoicesCount,
      fetchEstimatesCount,
      fetchExpensesCount,
      fetchShiftsCount,
      fetchIncidentsCount,
      fetchTimecardCount,
    ],
  );

  // STRATEGY 1: Realtime subscriptions for critical badges (invoices, estimates, timecards)
  useEffect(() => {
    if (!user) return;

    // Initial fetch (only if user has permission)
    if (permissions.hasInvoicesAccess) fetchInvoicesCount();
    if (permissions.hasTimeCardsAccess) fetchTimecardCount();
    if (permissions.hasEstimatesAccess) fetchEstimatesCount();

    const channel = supabase
      .channel("badge-updates")
      .on("postgres_changes", { event: "*", schema: "public", table: "invoices" }, () => {
        if (permissions.hasInvoicesAccess) fetchInvoicesCount();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "time_cards" }, () => {
        if (permissions.hasTimeCardsAccess) fetchTimecardCount();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "estimates" }, () => {
        if (permissions.hasEstimatesAccess) fetchEstimatesCount();
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [user, permissions, fetchInvoicesCount, fetchTimecardCount, fetchEstimatesCount]);

  // STRATEGY 2: AppState refresh for secondary badges
  useEffect(() => {
    if (!user) return;

    const handleAppStateChange = (state: AppStateStatus) => {
      if (state === "active") {
        // Refetch all badges when app becomes active (only if user has permission)
        if (permissions.hasInvoicesAccess) fetchInvoicesCount();
        if (permissions.hasTimeCardsAccess) fetchTimecardCount();
        if (permissions.hasEstimatesAccess) fetchEstimatesCount();
      }
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => subscription?.remove();
  }, [user, permissions, fetchInvoicesCount, fetchTimecardCount, fetchEstimatesCount]);

  // STRATEGY 3: Polling for badges without realtime subscriptions (shifts, incidents, expenses)
  useEffect(() => {
    if (!user) return;

    // Initial fetch (only if user has permission)
    if (permissions.hasShiftsAccess) fetchShiftsCount();
    if (permissions.hasIncidentsAccess) fetchIncidentsCount();
    if (permissions.hasExpensesAccess) fetchExpensesCount();

    const intervalId = setInterval(() => {
      if (permissions.hasShiftsAccess) fetchShiftsCount();
      if (permissions.hasIncidentsAccess) fetchIncidentsCount();
      if (permissions.hasExpensesAccess) fetchExpensesCount();
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [user, permissions, fetchShiftsCount, fetchIncidentsCount, fetchExpensesCount]);

  return (
    <BadgeContext.Provider
      value={{
        invoices,
        timecards,
        estimates,
        expenses,
        incidents,
        shifts,
        refreshBadge,
      }}
    >
      {children}
    </BadgeContext.Provider>
  );
}
